import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMoleculeDrawing } from '@peruselab/api-client/moleculeDrawings';
import { getIsomer } from '@peruselab/api-client/isomers';
import {
  aromaticBondIds,
  bondStereoValues,
  drawnBondOrders
} from '@peruselab/structure-editor/domain/documentSelectors';
import { reconstructDrawingDocument } from '@peruselab/structure-editor/domain/moleculeDrawingProjection';
import { drawingDocumentToSvg } from '@peruselab/structure-editor/rendering/svgExport';
import { fetchMolecule } from './moleculeCache';
import { SvgMarkup } from './SvgMarkup';

interface DrawingThumbnailProps {
  readonly drawingId: string;
  // #794 -- the drawing's last-update stamp: a re-saved drawing gets a fresh thumbnail.
  readonly version?: string | undefined;
  readonly size?: number;
  /**
   * 'paper' (result and My drawings cards): dark ink on white in every theme, like molecule cards.
   * 'theme' (the default; e.g. the Templates rail): ink follows the theme's text colour, and labels
   * get no backing rectangle -- a white one shows as white boxes on a dark theme
   * (owner report on #794). Bonds are already trimmed at labels.
   */
  readonly ink?: 'paper' | 'theme';
  /** #885 -- fill the parent's width (square) instead of a fixed `size`: the public drawing page. */
  readonly fill?: boolean;
}

// The molecule cards' ink (MoleculeThumbnail), so drawing and molecule cards match.
const PAPER_INK = '#1f2933';

type ThumbnailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly svg: string }
  | { readonly status: 'error'; readonly message: string };

// #269: a read-only structure image for a saved molecule-drawing id -- the Templates rail's
// favourites shelf. A stored drawing is the v11 `moleculeDrawing` container (an isomerRef, no
// inline structure), so this walks the same fetch chain `loadDrawingById` uses --
// drawing -> isomer -> molecule -> reconstructDrawingDocument -- then renders through the SVG
// pipeline the "Export SVG" button uses. Colour is `currentColor` so the thumbnail follows the
// rail's theme token rather than a baked-in hex.
//
// #794 -- also the My drawings feed: the drawing as saved, with its text, shapes and arrows (they
// travel in the isomer ref's annotations) and its own layout. Bond lengths keep the thumbnail
// normalization (#757 decision); user text is escaped by the SVG pipeline (#536).
//
// One in-flight promise per drawing id and version, memoised module-wide (the same drawing can
// sit in the rail and the feed). A rejected attempt is evicted for retry. Cost: three requests
// (drawing, isomer, molecule -- the molecule is shared through moleculeCache), made only when the
// card comes near the screen.
const svgByDrawingId = new Map<string, Promise<string>>();

export function drawingThumbnailSvg(
  drawingId: string,
  version: string | undefined,
  ink: 'paper' | 'theme'
): Promise<string> {
  const key = `${drawingId}@${version ?? ''}:${ink}`;
  const cached = svgByDrawingId.get(key);
  if (cached) return cached;
  const pending = (async () => {
    const drawing = await getMoleculeDrawing(drawingId);
    const [ref] = drawing.document.isomerRefs;
    const isomer = ref ? await getIsomer(ref.isomerId) : null;
    const molecule = isomer ? await fetchMolecule(isomer.moleculeId) : null;
    const document = reconstructDrawingDocument(drawing.document, molecule?.document, isomer?.document);
    const coordinateSize = {
      width: document.depiction.coordinateSet.width,
      height: document.depiction.coordinateSet.height
    };
    return drawingDocumentToSvg(
      document,
      {
        bondOrders: drawnBondOrders(document),
        aromaticBondIds: aromaticBondIds(document),
        stereoBonds: bondStereoValues(document),
        overriddenBondIds: new Set()
      },
      ink === 'paper' ? PAPER_INK : 'currentColor',
      coordinateSize,
      // #429 -- namespace this inlined SVG's <marker> ids (see MoleculeThumbnail).
      `d-${drawingId.replace(/[^A-Za-z0-9_-]/g, '')}-${ink}-`,
      {},
      [],
      [],
      // A theme-coloured thumbnail sits on the theme's own surface: no white label rectangles.
      ink === 'theme' ? { background: null } : {}
    );
  })();
  pending.catch(() => svgByDrawingId.delete(key));
  svgByDrawingId.set(key, pending);
  return pending;
}

export function DrawingThumbnail({
  drawingId,
  version,
  size = 88,
  ink = 'theme',
  fill = false
}: DrawingThumbnailProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<ThumbnailState>({ status: 'loading' });
  const rootRef = useRef<HTMLDivElement | null>(null);
  // #794 -- fetched only once the card is near the screen: a long, continuously scrolling feed
  // doesn't request every thumbnail up front.
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const element = rootRef.current;
    if (near || !element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near) return;
    let cancelled = false;
    setState({ status: 'loading' });
    drawingThumbnailSvg(drawingId, version, ink)
      .then((svg) => {
        if (!cancelled) setState({ status: 'ready', svg });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : t('drawingThumbnail.renderError')
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [drawingId, version, ink, near, t]);

  return (
    <div
      ref={rootRef}
      className={`drawing-thumbnail${ink === 'paper' ? ' drawing-thumbnail--paper' : ''}${fill ? ' drawing-thumbnail--fill' : ''}`}
      style={fill ? undefined : { width: size, height: size }}
    >
      {state.status === 'loading' ? <span className="drawing-thumbnail-status">{t('common.loading')}</span> : null}
      {state.status === 'error' ? (
        <span className="drawing-thumbnail-status drawing-thumbnail-error" title={state.message}>
          {t('drawingThumbnail.renderError')}
        </span>
      ) : null}
      {state.status === 'ready' ? (
        // Generated entirely by drawingDocumentToSvg -- numeric coordinates, fixed element symbols,
        // and the drawing's own text escaped as XML (#536) -- the same trust level as the
        // SVG-export download.
        <SvgMarkup className="drawing-thumbnail-svg" svg={state.svg} />
      ) : null}
    </div>
  );
}
